// Copyright 2023 Amazon.com, Inc. or its affiliates. All Rights Reserved.
import { Registry } from "@amzn/shortbread/dist/shortbread";
import { ShortbreadCookieMetadata } from "@customTypes/shortbreadCookie";
import {
    ClientMessageCookieNames,
    DEEP_LINK_LIST_LENGTH_MAX,
    DEEP_LINK_URL_LENGTH_MAX,
} from "./clientMessage";

/**
 * All Cookie Identifiers. Each cookie needs to be approved by legal. Please
 * see https://w.amazon.com/bin/view/CookieJar/Onboarding.
 *
 * @note Any updates to descriptions, names or sizes need to go through the
 * above mentioned validation process.
 */
export const CustomCookieIdentifier: Record<
    string,
    ShortbreadCookieMetadata
> = {
    /**
     * This cookie is a functional cookie and is used to make sure user only sees
     * the new user tutorial the first time they open the web app, and then set
     * the cookie to true when they finish the tutorial.
     */
    TUTORIAL: {
        name: "wsw-tutorial-finish",
        sizeInBytes: 512,
    },

    /**
     * This cookie is an essential cookie which enables a specific language to be
     * used for our webapp which could be different from their default browser
     * language.
     */
    WSW_LANGUAGE: {
        name: "wsw-language",
        sizeInBytes: 512,
    },

    /**
     * This cookie is a functional cookie and is used to store which warning /
     * non-critical error banners have been acknowledged
     */
    BANNER_STATE: {
        name: "wsw-banner-state",
        sizeInBytes: 512,
    },

    /**
     * This cookie is an essential cookie and is used to store information
     * to manage experimental features for the portal.
     */
    EXPERIMENTAL: {
        name: "wsw-experimental",
        sizeInBytes: 300,
    },

    /**
     * This cookie is an essential cookie and should be exclusively used
     * for maintaining session related state like streamingURL cache or session
     * specific essential information.
     */
    SESSION_STATE: {
        name: "wsw-session-state",
        sizeInBytes: 4096,
    },

    /**
     * This cookie is a functional cookie and is used to store user preferred toolbar settings
     * if they have changed any from previous session.
     */
    TOOLBAR_SETTINGS: {
        name: "wsw-toolbar-settings",
        sizeInBytes: 4096,
    },

    /**
     * This cookie is an essential cookie and is used to store the
     * DeepLink string in sessionStorage until it is passed to back-end
     */
    DEEPLINK: {
        name: "wsw-deeplink",
        sizeInBytes: 2048,
    },

    /**
     * This cookie is an essential cookie and is used to store the list of deep
     * links which will be sent to back-end.
     */
    DEEP_LINKS: {
        name: ClientMessageCookieNames.WSW_DEEP_LINKS,
        // Character in a valid URL should be ASCII which encoded size is one byte per character.
        // The length of array characters '[","]' needs to be counted.
        sizeInBytes:
            DEEP_LINK_URL_LENGTH_MAX * DEEP_LINK_LIST_LENGTH_MAX +
            DEEP_LINK_LIST_LENGTH_MAX * 4,
    },
};

/**
 * Cookie categorized by Legal Team. Only `essential` cookies are guaranteed
 * to be always accessible via shortbread wrappers.
 * Read: https://w.amazon.com/bin/view/Shortbread
 */
export const ShortbreadCookieRegistry: Registry = {
    "wsw-banner-state": { category: "functional" },
    "wsw-session-state": { category: "essential" },
    "wsw-experimental": { category: "essential" },
    "wsw-tutorial-finish": { category: "functional" },
    "wsw-language": { category: "essential" },
    "wsw-toolbar-settings": { category: "functional" },
    "wsw-deeplink": { category: "essential" },
    [CustomCookieIdentifier.DEEP_LINKS.name]: { category: "essential" },
};

export enum CookieCategory {
    Essential = "Essential",
    Performance = "Performance",
    Functional = "Functional",
    Advertising = "Advertising",
}
